import * as React from 'react';
import * as echarts from 'echarts/core';
import { FunnelChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    LegendComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';

import type {
    TitleComponentOption,
    TooltipComponentOption,
    LegendComponentOption,
    FunnelSeriesOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | TitleComponentOption
    | TooltipComponentOption
    | LegendComponentOption
    | FunnelSeriesOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    LegendComponent,
    FunnelChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

interface Props {
    totals: {
        visits: number;
        registered: number;
        updated_profile: number;
    };
}

const pct = (value: number, total: number) => {
    if (!total) {
        return '0.0%';
    }
    return `${((value / total) * 100).toFixed(1)}%`;
};

const CustomerProfileFunnelSummaryChart: React.FC<Props> = ({ totals }) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        const visits = Number(totals.visits) || 0;
        const registered = Number(totals.registered) || 0;
        const updatedProfile = Number(totals.updated_profile) || 0;
        const data = [
            {
                name: 'Visited',
                value: visits,
                rate: '100.0%',
            },
            {
                name: 'Registered',
                value: registered,
                rate: pct(registered, visits),
            },
            {
                name: 'Updated Profile',
                value: updatedProfile,
                rate: pct(updatedProfile, registered),
            },
        ];

        if (chartRef.current) {
            if (!chartInstance.current) {
                chartInstance.current = echarts.init(chartRef.current);
            }

            const option: ECOption = {
                tooltip: {
                    trigger: 'item',
                    formatter: (p: any) => {
                        const item = data[p.dataIndex];
                        return `${p.name}: ${Number(p.value).toLocaleString()}<br/>Conversion: ${item.rate}`;
                    },
                },
                legend: {
                    orient: 'vertical',
                    left: 'left',
                    data: data.map(item => item.name),
                },
                series: [
                    {
                        name: 'Customer Profile Funnel',
                        type: 'funnel',
                        left: '28%',
                        top: 20,
                        bottom: 20,
                        width: '62%',
                        sort: 'none',
                        gap: 4,
                        label: {
                            show: true,
                            formatter: (p: any) => {
                                const item = data[p.dataIndex];
                                return `${p.name}: ${Number(p.value).toLocaleString()} (${item.rate})`;
                            },
                        },
                        itemStyle: {
                            borderColor: '#fff',
                            borderWidth: 1,
                        },
                        data: data.map(item => ({
                            name: item.name,
                            value: item.value,
                        })),
                    },
                ],
            };

            chartInstance.current.setOption(option);
        }

        const resizeHandler = () => chartInstance.current?.resize();
        window.addEventListener('resize', resizeHandler);
        return () => {
            window.removeEventListener('resize', resizeHandler);
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, [totals]);

    return <div ref={chartRef} style={{ width: '100%', height: 320 }} />;
};

export default CustomerProfileFunnelSummaryChart;
